(() => {
  "use strict";

  const ZOOM_CAPTION_SELECTOR = "[data-zoom-caption]";
  const ZOOM_IMAGE_SELECTOR = "[data-zoom-image]";
  const THUMBS_TEMPLATE_SELECTOR = "[data-pswp-thumbs-template]";
  const SECTION_SELECTOR = "[data-section-type]";
  const THUMBS_CONTAINER_SELECTOR = ".pswp__thumbs";
  const PRODUCT_IMAGES_SELECTOR = "product-images";
  const PRODUCT_COMPONENT_SELECTOR = "product-component";
  const ACTIVE_VARIANT_GALLERY_SELECTOR = ".custom-variant-images.active";
  const DESKTOP_GALLERY_SELECTOR = ".custom-variant-images-wrapper";
  const MOBILE_GALLERY_SELECTOR = ".custom-variant-images-swiper";
  const DRAGGING_CLASS = "is-dragging";
  const SOLD_OUT_CLASS = "variant--soldout";
  const UNAVAILABLE_CLASS = "variant--unavailable";
  const GALLERY_CLASS = "pswp-zoom-gallery";
  const SINGLE_GALLERY_CLASS = "pswp-zoom-gallery--single";
  const IMAGE_SRC_ATTRIBUTE = "data-image-src";
  const IMAGE_WIDTH_ATTRIBUTE = "data-image-width";
  const IMAGE_HEIGHT_ATTRIBUTE = "data-image-height";

  class ZoomImages extends HTMLElement {
    constructor() {
      super();
      this.container = this.closest(SECTION_SELECTOR);
      this.zoomCaptions = this.container ? this.container.querySelector(ZOOM_CAPTION_SELECTOR) : null;
      this.thumbsContainer = document.querySelector(THUMBS_CONTAINER_SELECTOR);
      this.images = [];
      this.handleZoomClick = this.handleZoomClick.bind(this);
      this.handleZoomKeyup = this.handleZoomKeyup.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.handleZoomClick);
      this.addEventListener("keyup", this.handleZoomKeyup);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleZoomClick);
      this.removeEventListener("keyup", this.handleZoomKeyup);
    }

    handleZoomClick(event) {
      const image = event.target.closest?.(ZOOM_IMAGE_SELECTOR);
      if (!image || !this.contains(image) || !image.closest(ACTIVE_VARIANT_GALLERY_SELECTOR)) return;

      event.preventDefault();
      const productImages = image.closest(PRODUCT_IMAGES_SELECTOR);
      if (!productImages?.classList.contains(DRAGGING_CLASS)) this.openImage(image);
    }

    handleZoomKeyup(event) {
      if (event.code !== "Enter") return;

      const image = event.target.closest?.(ZOOM_IMAGE_SELECTOR);
      if (!image || !this.contains(image) || !image.closest(ACTIVE_VARIANT_GALLERY_SELECTOR)) return;

      event.preventDefault();
      this.openImage(image);
    }

    getZoomImages(target) {
      const gallery = target.closest(ACTIVE_VARIANT_GALLERY_SELECTOR);
      if (!gallery) return [];

      const gallerySelector = window.matchMedia("(max-width: 989px)").matches
        ? MOBILE_GALLERY_SELECTOR
        : DESKTOP_GALLERY_SELECTOR;
      const galleryViewport = gallery.querySelector(gallerySelector);

      return galleryViewport
        ? Array.from(galleryViewport.querySelectorAll(ZOOM_IMAGE_SELECTOR))
        : [];
    }

    openImage(target) {
      this.images = this.getZoomImages(target);
      const index = this.images.indexOf(target);
      if (index < 0) return;

      this.createZoom(index);
      if (window.a11y) window.a11y.lastElement = target;
    }

    createZoom(index) {
      const thumbsTemplate = this.container?.querySelector(THUMBS_TEMPLATE_SELECTOR)?.innerHTML || "";
      const items = this.images.map((image) => {
        const source = image.getAttribute(IMAGE_SRC_ATTRIBUTE);
        return {
          src: source,
          w: parseInt(image.getAttribute(IMAGE_WIDTH_ATTRIBUTE), 10),
          h: parseInt(image.getAttribute(IMAGE_HEIGHT_ATTRIBUTE), 10),
          msrc: source,
        };
      });

      if (!items.length) return;

      const colorScheme = this.getColorScheme();
      const options = {
        history: false,
        focus: false,
        index,
        mainClass:
          GALLERY_CLASS +
          (items.length === 1 ? " " + SINGLE_GALLERY_CLASS : "") +
          (colorScheme ? " " + colorScheme : ""),
        showHideOpacity: true,
        showAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ["caption-close", "title"],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: false,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: true,
        addCaptionHTMLFn: (item, captionElement, isFake) => {
          this.zoomCaption(item, captionElement, isFake);
        },
        getThumbBoundsFn: () => {
          const image = this.images[index];
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const bounds = image.getBoundingClientRect();
          return { x: bounds.left, y: bounds.top + scrollTop, w: bounds.width };
        },
      };

      new window.theme.LoadPhotoswipe(items, options);

      if (this.thumbsContainer && thumbsTemplate) {
        this.thumbsContainer.innerHTML = thumbsTemplate;
      }
    }

    zoomCaption(item, captionElement) {
      let caption = "";
      const captionContent = captionElement.children[0];

      if (this.zoomCaptions) {
        caption = this.zoomCaptions.innerHTML;
        if (this.zoomCaptions.closest("." + SOLD_OUT_CLASS)) {
          captionContent.classList.add(SOLD_OUT_CLASS);
        } else {
          captionContent.classList.remove(SOLD_OUT_CLASS);
        }

        if (this.zoomCaptions.closest("." + UNAVAILABLE_CLASS)) {
          captionContent.classList.add(UNAVAILABLE_CLASS);
        } else {
          captionContent.classList.remove(UNAVAILABLE_CLASS);
        }
      }

      captionContent.innerHTML = caption;
      return false;
    }

    getColorScheme() {
      const product = this.closest(PRODUCT_COMPONENT_SELECTOR);
      return product
        ? Array.from(product.classList).find((className) => className.startsWith("color-scheme"))
        : undefined;
    }
  }

  if (!customElements.get("zoom-images")) {
    customElements.define("zoom-images", ZoomImages);
  }
})();
